import React, { createContext, useContext, ReactNode, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserDetails } from '@/types/user';
import { useAuth as useFirebaseAuth } from './firebase-auth';
import { useLocation } from 'wouter';
import { authenticatedFetch } from '../lib/api-client';
import { getPendingInvite, clearPendingInvite, setPendingInvite } from '@/lib/invite-storage';
import { apiUrl } from '@/config/api';

interface AuthContextType {
  user: UserDetails | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refetch: () => void;
  authMethod: 'firebase';
  firebaseUser: any | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During lazy loading or initial render, the context might not be available yet
    // Return a safe default state instead of throwing to prevent crashes
    // This can happen during React Suspense boundary resolution
    console.warn('[Integrated Auth] Context not available - returning loading state');
    return {
      user: null,
      isLoading: true,
      isAuthenticated: false,
      refetch: () => {},
      authMethod: 'firebase' as const,
      firebaseUser: null,
    };
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // HMR-safe: Wrap useFirebaseAuth in try-catch to handle Hot Module Reload race conditions
  // During HMR, the FirebaseAuthProvider context might not be ready yet
  let firebaseUser: any | null = null;
  let firebaseLoading: boolean = true;
  let getAuthToken: (forceRefresh?: boolean) => Promise<string | null> = async () => null;

  try {
    const firebaseAuth = useFirebaseAuth();
    firebaseUser = firebaseAuth.user;
    firebaseLoading = firebaseAuth.loading;
    getAuthToken = firebaseAuth.getAuthToken;
  } catch (error) {
    // During HMR, the FirebaseAuthProvider context might be temporarily unavailable
    // Log the error but don't crash - just use the fallback values above
    if (error instanceof Error && error.message.includes('useAuth must be used within an AuthProvider')) {
      console.warn('[Integrated Auth] Firebase context not ready during HMR - using fallback values');
    } else {
      // Re-throw if it's a different error
      throw error;
    }
  }

  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  // Debug logging
  const debugLog = (message: string) => {
    console.log(`[Integrated Auth] ${message}`);
    if ((window as any).iosDebugLog) {
      (window as any).iosDebugLog(`[Integrated Auth] ${message}`);
    }
  };

  // Log immediately when provider is created
  console.log('[Integrated Auth] IntegratedAuthProvider component initialized');
  if ((window as any).iosDebugLog) {
    (window as any).iosDebugLog('[Integrated Auth] IntegratedAuthProvider component initialized');
  }

  // Firebase authentication only
  const { data: user, isLoading: userLoading, refetch } = useQuery<UserDetails>({
    queryKey: ["/api/user", firebaseUser?.uid || null],
    // Force query to return null immediately if signed out (prevents stale cache)
    initialData: () => {
      const signedOutFlag = localStorage.getItem('yapzap_signed_out');
      return signedOutFlag ? null : undefined;
    },
    queryFn: async () => {
      console.log('🔥 [DEBUG] ========== REACT QUERY: /api/user ==========');
      console.log('🔥 [DEBUG] Query function called!');
      console.log('🔥 [DEBUG] firebaseUser exists:', !!firebaseUser);
      console.log('🔥 [DEBUG] firebaseUser uid:', firebaseUser?.uid);
      console.log('🔥 [DEBUG] firebaseUser email:', firebaseUser?.email);
      console.log('🔥 [DEBUG] yapzap_signed_out flag:', localStorage.getItem('yapzap_signed_out'));
      console.log('🔥 [DEBUG] Starting API request to /api/user...');
      
      // Enhanced: Also try to get user directly from Firebase if React context firebaseUser is null
      if (!firebaseUser) {
        debugLog('No firebaseUser in context, checking directly with Firebase');

        // CRITICAL: Check if user intentionally signed out - if so, do NOT restore
        const signedOutFlag = localStorage.getItem('yapzap_signed_out');
        if (signedOutFlag) {
          debugLog('User signed out - skipping direct Firebase check');
          console.log('🔥 [React Query] User signed out, returning null');
          return null;
        }

        try {
          const { authService } = await import('@/lib/firebase-auth-service');
          const currentUser = await authService.getCurrentUser();
          if (currentUser) {
            debugLog(`Found Firebase user directly: ${currentUser.email}, proceeding with API call`);
            // Use the directly fetched user for the token
            const token = await currentUser.getIdToken(true);
            if (token) {
              const response = await authenticatedFetch('/api/user', token);
              if (response.ok) {
                const userData = await response.json();
                debugLog('Successfully fetched user data with direct Firebase user');
                return userData;
              }
            }
          }
        } catch (error) {
          debugLog(`Direct Firebase check failed: ${error}`);
        }

        console.log('🔥 [React Query] No firebaseUser available, returning null');
        return null;
      }

      try {
        // Try to get a fresh token
        console.log('🔥 [Auth Context] Attempting to get auth token...');
        const token = await getAuthToken(true); // Force refresh
        console.log('🔥 [Auth Context] Token received:', token ? 'Yes' : 'No');
        console.log('🔥 [Auth Context] Token length:', token?.length || 0);
        if (!token) {
          console.error('🔥 [Auth Context] ERROR: No token received from getAuthToken');
          return null;
        }

        console.log('🔥 [Auth Context] Making API request to /api/user...');
        const response = await authenticatedFetch('/api/user', token);

        if (!response.ok) {
          console.error('Firebase auth request failed:', response.status, response.statusText);
          
          // Log the actual response content for debugging
          try {
            const responseText = await response.text();
            console.error('🔥 [Auth Context] Response body:', responseText);
          } catch (e) {
            console.error('🔥 [Auth Context] Could not read response body');
          }
          
          if (response.status === 401) {
            // Try one more time with a force-refreshed token
            try {
              const freshToken = await getAuthToken(true);
              if (freshToken) {
                const retryResponse = await authenticatedFetch('/api/user', freshToken);
                
                if (retryResponse.ok) {
                  const userData = await retryResponse.json();
                  return userData;
                }
              }
            } catch (retryError) {
              console.error('Token refresh retry failed:', retryError);
            }
            
            return null;
          }
          throw new Error(`Firebase auth check failed: ${response.statusText}`);
        }

        let userData;
        try {
          const responseText = await response.text();
          console.log('🔥 [Auth Context] Raw response:', responseText);
          userData = JSON.parse(responseText);
        } catch (parseError) {
          console.error('🔥 [Auth Context] JSON parse error:', parseError);
          console.error('🔥 [Auth Context] Response was not valid JSON');
          return null;
        }
        console.log('🎯 Auth context - User data received:', userData);
        console.log('🖼️ Avatar URL from auth:', userData?.avatarImageUrl);
        return userData;
      } catch (error) {
        console.error('Error in auth query:', error);
        return null;
      }
    },
    enabled: (() => {
      // CRITICAL: Do NOT enable query if user intentionally signed out
      const signedOutFlag = localStorage.getItem('yapzap_signed_out');
      if (signedOutFlag) {
        console.log('🔥 [DEBUG] ========== QUERY DISABLED ==========');
        console.log('🔥 [DEBUG] User signed out flag detected:', signedOutFlag);
        console.log('🔥 [DEBUG] Query will NOT run');
        return false;
      }

      // Enhanced: Enable the query if we have a firebaseUser OR if we might have a user but context hasn't updated
      const isEnabled = (!!firebaseUser && !firebaseLoading) || (!firebaseUser && !firebaseLoading);
      console.log('🔥 [DEBUG] ========== QUERY ENABLED CHECK ==========');
      console.log('🔥 [DEBUG] No signout flag - checking auth state');
      console.log('🔥 [DEBUG] firebaseUser:', !!firebaseUser);
      console.log('🔥 [DEBUG] firebaseUserEmail:', firebaseUser?.email);
      console.log('🔥 [DEBUG] firebaseLoading:', firebaseLoading);
      console.log('🔥 [DEBUG] isEnabled:', isEnabled);
      console.log('🔥 [DEBUG] queryKey:', ["/api/user", firebaseUser?.uid || null]);
      return isEnabled;
    })(),
    retry: (failureCount, error) => {
      // Retry up to 3 times, but only for certain errors
      if (failureCount >= 3) return false;
      // Don't retry for 401/403 errors (auth issues)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status === 401 || status === 403) return false;
      }
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
    refetchOnWindowFocus: false,
    staleTime: 0, // Always consider data stale for immediate updates
    cacheTime: 0, // Don't cache to prevent auth state issues
  });

  // CRITICAL: Monitor signed-out flag and clear only USER-RELATED cache (not public endpoints)
  useEffect(() => {
    let hasCleared = false; // Only clear once per signed-out state

    const checkSignedOutFlag = () => {
      const signedOutFlag = localStorage.getItem('yapzap_signed_out');
      if (signedOutFlag && !hasCleared) {
        console.log('🚪 [React Query] Signed-out flag detected - clearing user-related cache only');
        hasCleared = true;

        // Only clear user-related queries, NOT public endpoints like leaderboards
        // This allows unauthenticated users to still see public content
        queryClient.setQueryData(["/api/user", firebaseUser?.uid || null], null);
        queryClient.setQueryData(["/api/user"], null);
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
        queryClient.invalidateQueries({ queryKey: ["/api/leagues/user"] });
        queryClient.invalidateQueries({ queryKey: ["/api/user/your-turn"] });
        queryClient.invalidateQueries({ queryKey: ["/api/user/deadlines"] });

        // Do NOT call queryClient.clear() as it wipes public endpoint caches
        // like leaderboards, hits, discover which should work for unauthenticated users
      }
    };

    // Check immediately on mount
    checkSignedOutFlag();

    // No need for interval - once cleared, stay cleared until page reload
    // This prevents constant cache clearing that blocks public queries
  }, [queryClient, firebaseUser?.uid]);

  // Force refetch when Firebase user changes
  useEffect(() => {
    if (firebaseUser && !firebaseLoading) {
      debugLog('Firebase user detected, triggering user data refetch');
      refetch();
    }
  }, [firebaseUser, firebaseLoading, refetch]);

  // ADDITIONAL: Force authentication check for Capacitor apps
  useEffect(() => {
    const checkCapacitorAuth = async () => {
      if (!firebaseUser && !firebaseLoading) {
        debugLog('No Firebase user in context, checking Capacitor native auth...');
        try {
          const { Capacitor } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
            debugLog('Native platform detected, checking for existing auth...');
            const { authService } = await import('@/lib/firebase-auth-service');
            
            // Force a check of the current user from native Firebase
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
              debugLog(`Found authenticated user in native Firebase: ${currentUser.email}`);
              // Force a refetch since we found a user
              refetch();
            } else {
              debugLog('No authenticated user found in native Firebase');
            }
          }
        } catch (error) {
          debugLog(`Capacitor auth check failed: ${error}`);
        }
      }
    };

    // Run the check after a short delay to allow Firebase to initialize
    const timeoutId = setTimeout(checkCapacitorAuth, 2000);
    return () => clearTimeout(timeoutId);
  }, [firebaseUser, firebaseLoading, refetch]);

  // Removed aggressive polling mechanism that was causing race conditions

  // Auto-redirect new users to onboarding
  useEffect(() => {
    if (user && user.username && user.username.startsWith('user_')) {
      // User has temporary username, needs onboarding
      const currentPath = window.location.pathname;
      console.log('🎯 [Onboarding Check] User has temp username:', user.username);
      console.log('🎯 [Onboarding Check] Current path:', currentPath);

      // If user is on invite page, extract invite code and redirect to onboarding with it
      if (currentPath.startsWith('/invite/')) {
        const inviteCode = currentPath.split('/invite/')[1];
        console.log('🎯 [Onboarding Check] User on invite page, extracting code:', inviteCode);

        if (inviteCode) {
          // Store invite code using persistent storage
          setPendingInvite(inviteCode);
          console.log('🎯 [Onboarding Check] Stored invite code, redirecting to onboarding...');

          setTimeout(() => {
            setLocation('/onboarding');
          }, 0);
          return;
        }
      }

      if (currentPath !== '/onboarding') {
        console.log('🎯 [Onboarding Check] Redirecting to /onboarding...');
        // Use setTimeout to prevent setState during render
        setTimeout(() => {
          setLocation('/onboarding');
        }, 0);
      } else {
        console.log('🎯 [Onboarding Check] Already on onboarding page, no redirect needed');
      }
    }
  }, [user, setLocation]);

  // Track if we've already processed the pending invite
  const pendingInviteProcessedRef = useRef(false);

  // Handle pending invite after successful authentication (for users who completed onboarding)
  useEffect(() => {
    const processPendingInvite = async () => {
      // Only process if:
      // 1. User is authenticated with a complete profile (not temp username)
      // 2. We haven't already processed in this session
      // 3. There's a pending invite
      // 4. We're not already on the join-success page
      const currentPath = window.location.pathname;

      if (
        user &&
        user.username &&
        !user.username.startsWith('user_') &&
        !pendingInviteProcessedRef.current &&
        currentPath !== '/join-success' &&
        currentPath !== '/onboarding'
      ) {
        const pendingInvite = getPendingInvite();

        if (pendingInvite) {
          console.log('🎫 [Auth] Found pending invite after auth:', pendingInvite);
          pendingInviteProcessedRef.current = true;

          try {
            // Get the auth token
            const token = await getAuthToken(true);
            if (!token) {
              console.error('🎫 [Auth] No auth token available for invite processing');
              return;
            }

            // First, get the league data from the invite code
            const leagueResponse = await fetch(apiUrl(`/api/leagues/invite/${pendingInvite}`), {
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });

            if (!leagueResponse.ok) {
              console.error('🎫 [Auth] Invalid invite code');
              clearPendingInvite();
              return;
            }

            const leagueData = await leagueResponse.json();
            console.log('🎫 [Auth] League data for invite:', leagueData);

            // Try to join the league
            const joinResponse = await fetch(apiUrl(`/api/leagues/${leagueData.id}/join`), {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ inviteCode: pendingInvite })
            });

            if (joinResponse.ok) {
              console.log('🎫 [Auth] Successfully auto-joined league!');
              clearPendingInvite();
              // Navigate to success page
              const encodedName = encodeURIComponent(leagueData.name || '');
              setLocation(`/join-success?league=${leagueData.id}&name=${encodedName}`);
            } else {
              const errorData = await joinResponse.json().catch(() => ({}));
              console.log('🎫 [Auth] Join response:', errorData);

              // Clear the pending invite regardless of outcome
              clearPendingInvite();

              // If already a member, go directly to the league
              if (errorData.message?.includes('already a member') || joinResponse.status === 409) {
                console.log('🎫 [Auth] User is already a member, navigating to league');
                setLocation(`/leagues/${leagueData.id}`);
              }
              // Otherwise, just let them continue normally
            }
          } catch (error) {
            console.error('🎫 [Auth] Error processing pending invite:', error);
            clearPendingInvite();
          }
        }
      }
    };

    processPendingInvite();
  }, [user, setLocation, getAuthToken]);

  // Simplified loading state management
  const isLoading = firebaseLoading || userLoading;

  // CRITICAL: Force user to null if signed-out flag exists (overrides React Query cache)
  const signedOutFlag = localStorage.getItem('yapzap_signed_out');
  const effectiveUser = signedOutFlag ? null : user;
  const isAuthenticated = !!effectiveUser;

  // Removed force navigation and nuclear option code that was causing race conditions
  const finalIsLoading = isLoading;
  const finalIsAuthenticated = isAuthenticated;

  // Debug logging for loading states (only log when core states change)
  React.useEffect(() => {
    debugLog(`Loading states - firebaseLoading: ${firebaseLoading}, userLoading: ${userLoading}, isLoading: ${isLoading}`);
  }, [firebaseLoading, userLoading]);

  React.useEffect(() => {
    debugLog(`Auth states - firebaseUser: ${!!firebaseUser}, user: ${!!user}, isAuthenticated: ${isAuthenticated}`);
  }, [!!firebaseUser, !!user]); // Only log when actual user state changes

  return (
    <AuthContext.Provider value={{
      user: effectiveUser || null,
      isLoading: finalIsLoading,
      isAuthenticated: finalIsAuthenticated,
      refetch,
      authMethod: 'firebase',
      firebaseUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
